import React, { useCallback, useMemo } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { ProductInformationSettingsV2 } from 'apis/offer-listing-service/types';
import { isDefined, localize } from 'common/utils';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { ItemQuantity } from 'modules/menu/items/types';
import { useOfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';
import { useIsProductFamilyAiGenerationEnabled } from 'modules/shared-menu/hooks/useIsProductFamilyAiGenerationEnabled';

import { Fields } from './Fields';
import { ProductFamilySuggestionTrackingState } from './Fields/ProductFamily';
import { WithOverrideToggle } from './Fields/WithOverrideToggle';
import { BaseField } from './Fields/types';
import * as S from './OfferDetailsFormFields.styles';
import { OfferTab, offerTabs } from './constants';
import { FormValues } from './types';
import { alcoholPercentageToPermilles, getProductHierarchyOptions, getTabsErrors } from './utils';
import { useOfferTabContext } from '../../context/OfferTabContext';
import { useVenueOfferCommissions } from '../../hooks/useVenueOfferCommissions';
import { Offer } from '../../types';
import { TabsContainer, TabbedSection, SectionTitle, Root, StyledTabs, SectionInfo } from '../CommonPanelStyles';
import { OptionBindings } from '../OptionBindings';
import { OptionBindingsFormSection } from '../OptionBindings/OptionBindingFormSection';
import { TabCountIcon } from '../TabCountIcon';

type OfferDetailsFormFieldsProps = {
  categoryId?: string | null;
  className?: string;
  sidePanelMode: BaseField['sidePanelMode'];
  offer?: Offer;
  primaryLanguage: Language;
  nutritionFactsFormat: 'text' | 'table';
  productInfoSettings: ProductInformationSettingsV2 | undefined;
  onChangeNutritionFactsFormat: (nutritionFactsFormat: 'text' | 'table') => void;
  offerQuantity?: ItemQuantity;
  updateProductFamilyTrackingState: (state: ProductFamilySuggestionTrackingState) => void;
};

const OfferDetailsFormFields = ({
  className,
  categoryId,
  sidePanelMode,
  offer,
  primaryLanguage,
  nutritionFactsFormat,
  productInfoSettings,
  onChangeNutritionFactsFormat,
  offerQuantity,
  updateProductFamilyTrackingState,
}: OfferDetailsFormFieldsProps) => {
  const intl = useIntl();
  const form = useForm<FormValues>();
  const { isShowingTabErrors, activeTabId, setActiveTabId } = useOfferTabContext();
  const { values, errors } = useFormState<FormValues>();
  const menuPermissions = useOfferingMenuPermissionsContext();
  const menuFeatures = useOfferingMenuFeaturesContext();
  const { vlmeConfig } = useSelectionInfoContext();
  const isSingleVenue = !!vlmeConfig?.isSingleVenue;
  const inventoryFieldName = isSingleVenue ? 'inventoryMode' : 'inventoryModeOverride';
  const { commissionsLoadedSuccessfully } = useVenueOfferCommissions();
  const isProductFamilyAiGenerationEnabled = useIsProductFamilyAiGenerationEnabled();
  /* Inventory mode is only available for retail. This allows restaurant merchants to turn off the inventory mode */
  const showInventorySection = useMemo(
    () => !!vlmeConfig && (menuFeatures.isRetail || offer?.inventory_mode === 'forced_out_of_stock'),
    [menuFeatures.isRetail, offer?.inventory_mode, vlmeConfig],
  );

  const showCommissions = useMemo(() => {
    return (
      sidePanelMode !== 'create' &&
      !!vlmeConfig &&
      commissionsLoadedSuccessfully &&
      (menuPermissions.customCommissionEdit || menuPermissions.customCommissionRead)
    );
  }, [
    sidePanelMode,
    commissionsLoadedSuccessfully,
    menuPermissions.customCommissionEdit,
    menuPermissions.customCommissionRead,
    vlmeConfig,
  ]);

  const menuLocalizer = useCallback(
    (localized: Localized | undefined, strict?: boolean) => localize(localized ?? [], primaryLanguage, strict),
    [primaryLanguage],
  );

  const fieldBaseProps: BaseField = {
    sidePanelMode,
    offer,
    categoryId,
    menuLocalizer,
  };

  const tabsErrors = useMemo(() => getTabsErrors(errors), [errors]);
  const formHasErrors = useMemo(() => Object.values(tabsErrors).some(errorCount => errorCount > 0), [tabsErrors]);

  const { alcohol_warning_permille_limit: alcoholWarningPermilleLimit } = useCountryConfigContext();

  const productHierarchyOptions = useMemo(
    () => getProductHierarchyOptions(productInfoSettings?.productHierarchyLevels, intl.locale),
    [intl.locale, productInfoSettings?.productHierarchyLevels],
  );

  const isProductHierarchyLevelRestricted = useCallback(
    (value: string): boolean =>
      !!productHierarchyOptions.find(option => option.value === value)?.tags.includes('tobacco'),
    [productHierarchyOptions],
  );

  const handleRestrictedPropertyChange = useCallback(
    ({
      productHierarchyLevelId,
      alcoholPercentage,
    }: {
      productHierarchyLevelId?: string | null;
      alcoholPercentage?: string | number;
    }) => {
      const isAlcoholPercentageExceedWarningLimit =
        typeof alcoholPercentage === 'number' &&
        alcoholPercentageToPermilles(alcoholPercentage) > alcoholWarningPermilleLimit;
      const isRestrictedProductFamily =
        productHierarchyLevelId && isProductHierarchyLevelRestricted(productHierarchyLevelId);

      if (!isAlcoholPercentageExceedWarningLimit && !isRestrictedProductFamily) {
        return;
      }

      form.batch(() => {
        form.change('isRestrictedItem', true);
        form.change('isAgeRestricted', true);
        form.change('ageLimit', undefined);
      });
    },
    [alcoholWarningPermilleLimit, form, isProductHierarchyLevelRestricted],
  );

  const enabledTabs = useMemo(
    () =>
      [
        {
          id: offerTabs.DETAILS,
          label: intl.formatMessage({ id: 'shared-menu.editor.offer-tabs.details' }),
          rightContent: <TabCountIcon variant="error" count={isShowingTabErrors ? tabsErrors.DETAILS : 0} />,
        },
        {
          id: offerTabs.ADVANCED,
          label: intl.formatMessage({ id: 'shared-menu.editor.offer-tabs.advanced' }),
          rightContent: <TabCountIcon variant="error" count={isShowingTabErrors ? tabsErrors.ADVANCED : 0} />,
        },
        {
          id: offerTabs.AVAILABILITY,
          label: intl.formatMessage({ id: 'shared-menu.editor.offer-tabs.availability' }),
          rightContent: <TabCountIcon variant="error" count={isShowingTabErrors ? tabsErrors.AVAILABILITY : 0} />,
        },
        {
          id: offerTabs.OPTIONS,
          label: intl.formatMessage({ id: 'shared-menu.editor.offer-tabs.options' }),
        },
      ].filter(isDefined),
    [intl, isShowingTabErrors, tabsErrors],
  );

  return (
    <>
      <TabsContainer>
        {formHasErrors && isShowingTabErrors && (
          <S.StyledAlert variant="error">
            <FormattedMessage id="shared-menu.editor.error-messages.fix-errors" />
          </S.StyledAlert>
        )}
        <StyledTabs activeTabId={activeTabId} items={enabledTabs} onChange={(id: OfferTab) => setActiveTabId(id)} />
      </TabsContainer>
      <Root className={className}>
        <TabbedSection $isShown={activeTabId === offerTabs.DETAILS}>
          <SectionTitle>
            <FormattedMessage id="menu-item.details-heading" />
          </SectionTitle>

          <Fields.CatalogProduct
            {...fieldBaseProps}
            nutritionSettings={productInfoSettings?.nutritionValues || []}
            handleRestrictedPropertyChange={handleRestrictedPropertyChange}
          />

          <Fields.Category {...fieldBaseProps} />
          <Fields.Name {...fieldBaseProps} />
          <Fields.Brand {...fieldBaseProps} />
          <Fields.Description
            {...fieldBaseProps}
            primaryLanguage={primaryLanguage}
            productHierarchyOptions={productHierarchyOptions}
          />
          {isProductFamilyAiGenerationEnabled ? (
            <Fields.ProductFamily
              {...fieldBaseProps}
              handleRestrictedPropertyChange={handleRestrictedPropertyChange}
              productHierarchyOptions={productHierarchyOptions}
              isVenueLevel={!!vlmeConfig}
              updateProductFamilyTrackingState={updateProductFamilyTrackingState}
            />
          ) : (
            <Fields.ProductHierarchy
              {...fieldBaseProps}
              handleRestrictedPropertyChange={handleRestrictedPropertyChange}
              productHierarchyOptions={productHierarchyOptions}
            />
          )}

          <WithOverrideToggle
            // title={intl.formatMessage({
            //   id: 'shared-menu.venue-level-menu-editor.overrides.menu-fields.price-discount',
            // })}
            title="Price and Discount Override"
            sidePanelMode={sidePanelMode}
            rowGroup="PRICE_AND_DISCOUNT"
          >
            <Fields.Price {...fieldBaseProps} />
            <Fields.Discount {...fieldBaseProps} />
          </WithOverrideToggle>
          <Fields.Vat {...fieldBaseProps} />
          <Fields.ItemType {...fieldBaseProps} />
          <Fields.UnitDetails {...fieldBaseProps} />
          <Fields.AlcoholPercentage
            {...fieldBaseProps}
            handleRestrictedPropertyChange={handleRestrictedPropertyChange}
          />
          <Fields.Caffeine {...fieldBaseProps} />
          <Fields.OfferId {...fieldBaseProps} />

          {menuPermissions.posIdField && (
            <WithOverrideToggle
              // title={intl.formatMessage({ id: 'shared-menu.venue-level-menu-editor.overrides.menu-fields.pos-id' })}
              title="POS ID Override"
              sidePanelMode={sidePanelMode}
              rowGroup="POS_ID"
            >
              <Fields.PosId {...fieldBaseProps} />
            </WithOverrideToggle>
          )}

          <Fields.Gtin {...fieldBaseProps} />
          <WithOverrideToggle
            // title={intl.formatMessage({ id: 'shared-menu.venue-level-menu-editor.overrides.menu-fields.merchant-sku' })}
            title="Merchant SKU Override"
            sidePanelMode={sidePanelMode}
            rowGroup="MERCHANT_SKU"
          >
            <Fields.MerchantSku {...fieldBaseProps} />
          </WithOverrideToggle>
          <Fields.Images {...fieldBaseProps} />

          {showCommissions && (
            <>
              <SectionTitle>
                <FormattedMessage id={'menu-item.custom-commission'} />
              </SectionTitle>
              <Fields.CommissionHomedelivery {...fieldBaseProps} />
              <Fields.CommissionTakeaway {...fieldBaseProps} />
              <Fields.CommissionEatin {...fieldBaseProps} />
            </>
          )}
        </TabbedSection>

        <TabbedSection $isShown={activeTabId === offerTabs.ADVANCED}>
          <SectionTitle>
            <FormattedMessage id={'menu-item.advanced-details'} />
          </SectionTitle>

          <Fields.DietaryPreferences {...fieldBaseProps} />
          <Fields.CountryOfOrigin {...fieldBaseProps} />
          <Fields.Ingredients {...fieldBaseProps} />
          <Fields.Additives {...fieldBaseProps} />
          <Fields.Allergens {...fieldBaseProps} />
          <Fields.NutritionFacts
            {...fieldBaseProps}
            nutritionSettings={productInfoSettings?.nutritionValues || []}
            activeFieldType={nutritionFactsFormat}
            onChangeFieldType={onChangeNutritionFactsFormat}
          />
          <Fields.UsageInstructions {...fieldBaseProps} />
          <Fields.MoreInformation {...fieldBaseProps} />
          <Fields.ProducerInformation {...fieldBaseProps} />
          <Fields.DistributorInformation {...fieldBaseProps} />
          {menuFeatures.bundleItems && <Fields.BundleOffer {...fieldBaseProps} />}
          {menuFeatures.bottleDeposits && <Fields.DepositOption {...fieldBaseProps} />}
          {menuFeatures.mandatoryWarnings && <Fields.MandatoryWarnings {...fieldBaseProps} />}
          {(menuFeatures.overTheCounterHyperlinkAllowed || menuFeatures.externalInfoHyperlinkAllowed) && (
            <Fields.ExternalHyperlink {...fieldBaseProps} />
          )}
        </TabbedSection>

        <TabbedSection $isShown={activeTabId === offerTabs.AVAILABILITY}>
          <SectionTitle>
            <FormattedMessage id={'menu-item.availability-and-restrictions'} />
          </SectionTitle>

          <WithOverrideToggle
            // title={intl.formatMessage({ id: 'shared-menu.venue-level-menu-editor.overrides.menu-fields.availability' })}
            title="Availability Override"
            sidePanelMode={sidePanelMode}
            rowGroup="AVAILABILITY"
          >
            <Fields.Enabled {...fieldBaseProps} />
            <Fields.Availability {...fieldBaseProps} />
          </WithOverrideToggle>

          <WithOverrideToggle
            // title={intl.formatMessage({ id: 'shared-menu.venue-level-menu-editor.overrides.menu-fields.visibility' })}
            title="Visibility Override"
            sidePanelMode={sidePanelMode}
            rowGroup="VISIBILITY"
          >
            <Fields.ItemVisibility {...fieldBaseProps} />
          </WithOverrideToggle>

          <Fields.FirstTimeCustomers {...fieldBaseProps} />
          {menuFeatures.hasWoltPlusSpecialsEnabled && <Fields.EnableWoltPlus {...fieldBaseProps} />}
          <Fields.PerPurchase {...fieldBaseProps} />
          <Fields.RestrictedItem
            {...fieldBaseProps}
            productHierarchyOptions={productHierarchyOptions}
            productHierarchies={productInfoSettings?.productHierarchyLevels || []}
          />
          <Fields.DeliveryMethods {...fieldBaseProps} />
          <Fields.CourierRestrictions {...fieldBaseProps} />

          {showInventorySection && (
            <>
              <SectionTitle>
                <FormattedMessage id={'menu-item.inventory-management'} />
                <SectionInfo>
                  <FormattedMessage id="inventory-management.how-to-use" />
                </SectionInfo>
              </SectionTitle>
              <Fields.InventoryType {...fieldBaseProps} isSingleVenue={isSingleVenue} key={activeTabId} />
              {values[inventoryFieldName] !== 'limited_quantity' && (
                <Fields.InventoryStock
                  {...fieldBaseProps}
                  offerQuantity={offerQuantity}
                  isSingleVenue={isSingleVenue}
                />
              )}
            </>
          )}

          {!!vlmeConfig && (
            <>
              <SectionTitle>
                <FormattedMessage id={'menu-item.limited-quantity'} />
                <SectionInfo>
                  <FormattedMessage id="limited-quantity.how-to-use" />
                </SectionInfo>
              </SectionTitle>
              <Fields.LimitedQuantity {...fieldBaseProps} isSingleVenue={isSingleVenue} />
              {values[inventoryFieldName] === 'limited_quantity' && (
                <Fields.CurrentAmount {...fieldBaseProps} offerQuantity={offerQuantity} />
              )}
            </>
          )}
        </TabbedSection>

        <TabbedSection $isShown={activeTabId === offerTabs.OPTIONS}>
          {sidePanelMode === 'create' && (
            <OptionBindingsFormSection
              isWeightedItem={values.weightType === 'weighted-items'}
              primaryLanguage={primaryLanguage}
            />
          )}
          {!!offer && (
            <OptionBindings
              enableReordering={sidePanelMode === 'preview'}
              isWeightedItem={values.weightType === 'weighted-items'}
              offer={offer}
            />
          )}
        </TabbedSection>
      </Root>
    </>
  );
};

export default OfferDetailsFormFields;
